import { useEffect, useRef, useState } from 'react';

/**
 * Owns the "show conversation vs. skeleton" gate and the scroll-to-bottom when
 * a conversation is revealed.
 *
 * - Re-shows the shimmer skeleton when switching conversations while history
 *   loads. Only `isLoadingDetails` (history fetch) hides messages — streaming
 *   must not, or the assistant reply would be covered by the skeleton.
 * - Scrolls to the latest message whenever the conversation is *revealed* after
 *   loading — both the initial load and switching to another conversation
 *   (where the previous message count is non-zero, so a plain 0 -> N check
 *   would miss it and leave the view scrolled to the top).
 */
export function useConversationHistoryView(params: {
  currentConversationId: string | null | undefined;
  isLoadingDetails: boolean;
  messagesLength: number;
  scrollToBottom: () => void;
}): { showConversation: boolean } {
  const { currentConversationId, isLoadingDetails, messagesLength, scrollToBottom } = params;
  const [showConversation, setShowConversation] = useState(false);
  const prevShowRef = useRef(false);

  useEffect(() => {
    setShowConversation(false);
  }, [currentConversationId]);

  useEffect(() => {
    if (isLoadingDetails) {
      setShowConversation(false);
      return;
    }
    const timer = setTimeout(() => {
      setShowConversation(true);
    }, 300);
    return () => clearTimeout(timer);
  }, [isLoadingDetails, currentConversationId]);

  // On the reveal transition (skeleton -> messages), jump to the latest message.
  // The container was `display:none` while loading, so defer past layout before
  // measuring scroll height.
  useEffect(() => {
    const wasShowing = prevShowRef.current;
    prevShowRef.current = showConversation;
    if (showConversation && !wasShowing && messagesLength > 0) {
      const t = setTimeout(() => scrollToBottom(), 80);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [showConversation, messagesLength, scrollToBottom]);

  return { showConversation };
}
